import React from 'react'
import { css, Global } from '@emotion/core'
import { useTranslation } from 'react-i18next'
import { Controller, useForm } from 'react-hook-form'
import { Button } from '@/components/atoms/button'
import { Select, Option } from '@/components/atoms/select'
import { Label } from '@/components/molecules/label'
import { DateRangeField } from '@/components/molecules/date-range-field'
import {
  modalTitleStyle,
  modalItemWrapperStyle,
  modalFooterStyle,
} from '@/components/pages/setting/guest-room/room/_room/modal/modal-layout'
import {
  roomRateModalOverlayStyle,
  roomRateModalPanelPlacementStyle,
  roomRateModalSectionStyle,
  roomRatePickerPortalStyle,
} from '@/components/pages/setting/room-rate/shared-styles'
import type { PriceRank } from '@/models/room-rate/price-rank'
import { RATE_CALENDAR_WEEKDAY_KEYS } from '@/models/room-rate/rate-calendar'
import type { WeekdayIndex } from '@/models/room-rate/rate-calendar'

export type BulkSettingFormValues = {
  beginDate: string
  endDate: string
  weekdays: WeekdayIndex[]
  priceRankId: string
}

type Props = {
  ranks: PriceRank[]
  minDate: string
  defaultBeginDate: string
  defaultEndDate: string
  /** 期間が空のときにカレンダーで開く月（YYYY-MM-DD） */
  visibleMonth: string
  onClickCancel: () => void
  onClickChange: (values: BulkSettingFormValues) => void | Promise<void>
}

export const BulkSettingModal: React.FC<Props> = ({
  ranks,
  minDate,
  defaultBeginDate,
  defaultEndDate,
  visibleMonth,
  onClickCancel,
  onClickChange,
}) => {
  const { t } = useTranslation()
  const {
    control,
    handleSubmit,
    setValue,
    watch,
    formState: { isValid },
  } = useForm<BulkSettingFormValues>({
    mode: 'onChange',
    defaultValues: {
      beginDate: defaultBeginDate,
      endDate: defaultEndDate,
      weekdays: [],
      priceRankId: '',
    },
  })

  const beginDate = watch('beginDate')
  const endDate = watch('endDate')

  return (
    <div css={roomRateModalOverlayStyle}>
      <Global styles={roomRatePickerPortalStyle} />
      <div css={[modalSizeStyle, roomRateModalPanelPlacementStyle]}>
        <div css={modalTitleStyle}>{t('Bulk setting')}</div>
        <div css={roomRateModalSectionStyle}>
          <div css={modalItemWrapperStyle}>
            <div css={fieldBlockStyle}>
              <Label label={t('Period')} required />
              <div css={dateRangeWrapStyle}>
                <DateRangeField
                  beginDate={beginDate}
                  endDate={endDate}
                  withPortal
                  showSelectedPeriod
                  emptyPeriodVisibleMonth={visibleMonth}
                  isOutsideRange={day => day.format('YYYY-MM-DD') < minDate}
                  changeRangeDate={(begin, end) => {
                    // プリセットは isOutsideRange を通らないため、ここで minDate 未満をクリップ
                    const clippedBegin = begin && begin < minDate ? minDate : begin || ''
                    let clippedEnd = end && end < minDate ? minDate : end || ''
                    if (clippedBegin && clippedEnd && clippedEnd < clippedBegin) {
                      clippedEnd = clippedBegin
                    }
                    setValue('beginDate', clippedBegin, { shouldValidate: true })
                    setValue('endDate', clippedEnd, { shouldValidate: true })
                  }}
                />
              </div>
              <Controller
                name="beginDate"
                control={control}
                rules={{ required: true, validate: value => !!value && value >= minDate }}
                render={() => <input type="hidden" />}
              />
              <Controller
                name="endDate"
                control={control}
                rules={{
                  required: true,
                  validate: (value, formValues) => !!value && value >= minDate && value >= (formValues.beginDate || ''),
                }}
                render={() => <input type="hidden" />}
              />
            </div>

            <div css={fieldBlockStyle}>
              <Label label={t('Day of the week')} />
              <Controller
                name="weekdays"
                control={control}
                render={({ field: { onChange, value } }) => (
                  <Select
                    mode="multiple"
                    // antd は複数選択だと既定で矢印を出さないため明示する
                    showArrow
                    allowClear
                    placeholder={t('Please select')}
                    getPopupContainer={() => document.body}
                    dropdownStyle={{ zIndex: 400 }}
                    value={(value || []).map(String)}
                    onChange={vals => {
                      const selected = (Array.isArray(vals) ? vals : vals != null ? [vals] : []).map(String)
                      onChange(selected.map(v => Number(v) as WeekdayIndex))
                    }}
                    customStyle={selectOverrideStyle}
                  >
                    {RATE_CALENDAR_WEEKDAY_KEYS.map(day => (
                      <Option key={day} value={String(day)}>
                        {t(`rateCalendar.weekday.${day}`)}
                      </Option>
                    ))}
                  </Select>
                )}
              />
            </div>

            <div css={fieldBlockStyle}>
              <Label label={t('Rate rank after change')} required />
              <Controller
                name="priceRankId"
                control={control}
                rules={{ required: true }}
                render={({ field: { onChange, value } }) => (
                  <Select
                    placeholder={t('Please select')}
                    value={value || undefined}
                    onChange={onChange}
                    getPopupContainer={() => document.body}
                    dropdownStyle={{ zIndex: 400 }}
                    customStyle={selectOverrideStyle}
                  >
                    {ranks.map(rank => (
                      <Option key={rank.id} value={rank.id}>
                        {rank.shortName}
                      </Option>
                    ))}
                  </Select>
                )}
              />
            </div>
          </div>
        </div>
        <div css={modalFooterStyle}>
          <Button buttonType={3} width={110} height={38} marginRight={16} onClick={onClickCancel}>
            {t('Cancel')}
          </Button>
          <Button
            buttonType={isValid ? 1 : 5}
            width={110}
            height={38}
            marginRight={32}
            isDisabled={!isValid}
            onClick={handleSubmit(onClickChange)}
          >
            {t('Change')}
          </Button>
        </div>
      </div>
    </div>
  )
}

const modalSizeStyle = css({
  width: 600,
  height: 'auto',
  minHeight: 420,
  borderRadius: 5,
  overflow: 'hidden',
  boxShadow: '0px 0px 6px rgba(0, 0, 0, 0.1)',
  background: '#fff',
})

const fieldBlockStyle = css({
  marginBottom: 24,
  '&:last-of-type': {
    marginBottom: 0,
  },
})

const selectOverrideStyle = css({
  paddingBottom: 0,
  width: '100%',
})

const dateRangeWrapStyle = css({
  '.DateRangePickerInput': {
    borderRadius: 30,
    border: '1px solid #ccc',
    padding: '0 8px',
  },
})
